// /home/vitruvio/puntum-frontend/src/App.jsx

import React, { useState, Component } from 'react'
import { AuthProvider } from './context/AuthContext'
import { MonedaProvider } from './context/MonedaContext'
import Login from './pages/Login'
import Dashboard from './pages/Dashboard'
import Ventas from './pages/Ventas'
import Inventario from './pages/Inventario'
import Sucursales from './pages/Sucursales'
import Reportes from './pages/Reportes'
import Historial from './pages/Historial'
import Contactos from './pages/Contactos'
import Movimientos from './pages/Movimientos'
import Auditoria from './pages/Auditoria'
import Usuarios from './pages/Usuarios'
import Suscripcion from './pages/Suscripcion'
import Configuracion from './pages/Configuracion'
import Facturas from './pages/Facturas'
import Sidebar from './components/Sidebar'
import Gatekeeper from './components/Gatekeeper'

class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, errorInfo) {
    console.error('🔴 CRASH DETALLADO:', error)
    console.error('📍 Stack Trace:', error?.stack)
    console.error('📋 Error Info:', errorInfo)
    this.setState({ errorInfo })
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '2rem', background: '#ffffff', borderRadius: '12px', border: '2px solid #ef4444', margin: '2rem' }}>
          <h2 style={{ color: '#ef4444', margin: '0 0 0.5rem 0' }}>⚠️ Se produjo un inconveniente en esta vista</h2>
          <p style={{ color: '#64748b', margin: '0 0 1rem 0' }}>Hubo un problema al procesar los datos del módulo.</p>
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <details style={{ margin: '1rem 0', padding: '1rem', background: '#f8fafc', borderRadius: '6px', border: '1px solid #cbd5e1' }}>
              <summary style={{ cursor: 'pointer', fontWeight: '600', color: '#0f172a' }}>Detalles del Error (dev only)</summary>
              <pre style={{ margin: '0.5rem 0 0 0', fontSize: '0.75rem', overflow: 'auto', color: '#ef4444' }}>
                {this.state.error?.toString()}
              </pre>
            </details>
          )}
          <button
            onClick={() => this.setState({ hasError: false, error: null, errorInfo: null })}
            style={{ padding: '0.5rem 1rem', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
            Reintentar Carga
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

export default function App() {
  // 1️⃣ Estado del Token
  const [token, setToken] = useState(() => localStorage.getItem('puntum_token'))

  // 2️⃣ Estado Dinámico del Usuario
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem('puntum_user')
    if (!savedUser) return null
    try {
      return JSON.parse(savedUser)
    } catch (e) {
      console.error("Error al parsear usuario inicial:", e)
      return null
    }
  })

  const [currentPage, setCurrentPage] = useState('dashboard')

  // 3️⃣ Cierre de Sesión Limpio
  const handleLogout = () => {
    localStorage.removeItem('puntum_token')
    localStorage.removeItem('puntum_user')
    localStorage.removeItem('puntum_sucursal_activa')
    setToken(null)
    setUser(null)
    setCurrentPage('dashboard')
  }

  // 4️⃣ Confirmación de Inicio de Sesión / Registro
  const handleLoginSuccess = (userData) => {
    const savedToken = localStorage.getItem('puntum_token')
    if (savedToken) {
      setToken(savedToken)
    } else if (typeof userData === 'string') {
      localStorage.setItem('puntum_token', userData)
      setToken(userData)
    }

    const savedUser = localStorage.getItem('puntum_user')
    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser))
      } catch (e) {
        setUser(userData)
      }
    } else if (typeof userData === 'object' && userData !== null) {
      setUser(userData)
    }
  }

  // 🔒 Si no hay token guardado, forzar Login
  if (!token) {
    return <Login onLoginSuccess={handleLoginSuccess} />
  }

  const renderPage = () => {
    switch (currentPage) {
      case 'dashboard': 
        return <Dashboard user={user} />
      case 'ventas': 
        return (
          <Gatekeeper onNavigate={setCurrentPage}>
            <Ventas user={user} />
          </Gatekeeper>
        )
      case 'inventario':
        return (
          <Gatekeeper onNavigate={setCurrentPage}>
            <Inventario user={user} onNavigate={setCurrentPage} />
          </Gatekeeper>
        )
      case 'facturas':
        return (
          <Gatekeeper onNavigate={setCurrentPage}>
            <Facturas user={user} />
          </Gatekeeper>
        )
      case 'sucursales': 
        return <Sucursales user={user} />
      case 'reportes': 
        return (
          <Gatekeeper onNavigate={setCurrentPage}>
            <Reportes user={user} />
          </Gatekeeper>
        )
      case 'historial': 
        return <Historial user={user} />
      case 'contactos': 
        return <Contactos user={user} />
      case 'movimientos': 
        return (
          <Gatekeeper onNavigate={setCurrentPage}>
            <Movimientos user={user} />
          </Gatekeeper>
        )
      case 'auditoria': 
        return <Auditoria user={user} />
      case 'usuarios': 
        return <Usuarios user={user} />
      case 'suscripcion': 
        return <Suscripcion user={user} />
      case 'configuracion': 
        return <Configuracion user={user} />
      default: 
        return <Dashboard user={user} />
    }
  }

  return (
    <ErrorBoundary>
      <AuthProvider>
        <MonedaProvider>
          <div className="app">
            <Sidebar 
              user={user} 
              currentPage={currentPage} 
              onNavigate={setCurrentPage} 
              activeTab={currentPage} 
              setActiveTab={setCurrentPage} 
              onLogout={handleLogout} 
            />
            
            <main className="main-content">
              <ErrorBoundary key={currentPage}>
                {renderPage()}
              </ErrorBoundary>
            </main>
          </div>
        </MonedaProvider>
      </AuthProvider>
    </ErrorBoundary>
  )
}